// Email utility functions
// Sends emails via the Netlify send-email function

const EMAIL_ENDPOINT = '/.netlify/functions/send-email';

/**
 * Send an appointment reminder email
 * @param {Object} data - { clientEmail, clientName, date, time, address, teamNames }
 */
export const sendAppointmentReminder = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'appointment_reminder',
        data
      })
    });
    
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send email');
    return result;
  } catch (error) {
    console.error('Error sending appointment reminder:', error);
    throw error;
  }
};

/**
 * Send an estimate email
 * @param {Object} data - { clientEmail, clientName, estimateTotal, approvalLink, propertyAddress, services }
 */
export const sendEstimateEmail = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'estimate_sent',
        data
      })
    });
    
    let result;
    try {
      result = await response.json();
    } catch (parseError) {
      throw new Error(`Email server returned invalid response (status ${response.status})`);
    }
    
    if (!response.ok) {
      throw new Error(result.error || result.details || `Email failed with status ${response.status}`);
    }
    return result;
  } catch (error) {
    console.error('Error sending estimate email:', error);
    throw error;
  }
};

/**
 * Send a welcome email to new clients
 * @param {Object} data - { clientEmail, clientName, portalLink, firstAppointmentDate, firstAppointmentTime, address }
 */
export const sendWelcomeEmail = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'welcome',
        data
      })
    });
    
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send email');
    return result;
  } catch (error) {
    console.error('Error sending welcome email:', error);
    throw error;
  }
};

/**
 * Format time for email display
 * @param {string} time24 - Time in 24h format (e.g., "09:00")
 * @returns {string} Formatted time (e.g., "9:00 AM")
 */
export const formatTimeForEmail = (time24) => {
  if (!time24) return 'Morning (8am-12pm)';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Send estimate approved notification to staff
 * @param {Object} data - { clientName, clientEmail, clientPhone, propertyAddress, selectedFrequency, recurringPrice, deepCleanPrice, selectedAddons, clientNotes }
 */
export const sendEstimateApprovedNotification = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'estimate_approved',
        data
      })
    });
    
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send email');
    return result;
  } catch (error) {
    console.error('Error sending estimate approved notification:', error);
    throw error;
  }
};

/**
 * Send weekly schedule email to staff
 * @param {Object} data - { recipients, subject, scheduleText, weekLabel }
 */
export const sendScheduleEmail = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'schedule',
        data
      })
    });
    
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send email');
    return result;
  } catch (error) {
    console.error('Error sending schedule email:', error);
    throw error;
  }
};

/**
 * Send walkthrough invite email with Google Calendar link.
 * Sends to admin and optionally to the client.
 * @param {Object} data - { adminEmail, clientEmail, clientName, date, time, durationMinutes, address, notes, sendToClient, calendarUrl }
 */
export const sendWalkthroughInvite = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'walkthrough_invite',
        data
      })
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send walkthrough invite');
    return result;
  } catch (error) {
    console.error('Error sending walkthrough invite:', error);
    throw error;
  }
};

/**
 * Send a PIN reset email with a secure reset link
 * @param {Object} data - { clientEmail, clientName, resetLink, clientId, commLogId }
 */
export const sendPinResetEmail = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'pin_reset',
        data
      })
    });

    let result;
    try {
      result = await response.json();
    } catch (parseError) {
      throw new Error(`Email server returned invalid response (status ${response.status})`);
    }

    if (!response.ok) {
      throw new Error(result.error || result.details || `Email failed with status ${response.status}`);
    }
    return result;
  } catch (error) {
    console.error('Error sending PIN reset email:', error);
    throw error;
  }
};

/**
 * Trigger the weekly recap email manually
 * Calls the Netlify weekly-recap function directly
 */
export const sendWeeklyRecap = async () => {
  try {
    const response = await fetch('/.netlify/functions/weekly-recap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });

    let result;
    try {
      result = await response.json();
    } catch (parseError) {
      throw new Error(`Weekly recap returned invalid response (status ${response.status})`);
    }

    if (!response.ok) {
      throw new Error(result.error || `Weekly recap failed with status ${response.status}`);
    }
    return result;
  } catch (error) {
    console.error('Error sending weekly recap:', error);
    throw error;
  }
};

/**
 * Send a follow-up email for a lead/estimate
 * @param {Object} data - { clientEmail, clientName, estimateTotal, approvalLink, propertyAddress, serviceLabel, dateSent }
 */
export const sendLeadFollowUpEmail = async (data) => {
  try {
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'lead_follow_up',
        data
      })
    });

    let result;
    try {
      result = await response.json();
    } catch (parseError) {
      throw new Error(`Email server returned invalid response (status ${response.status})`);
    }

    if (!response.ok) {
      throw new Error(result.error || result.details || `Email failed with status ${response.status}`);
    }
    return result;
  } catch (error) {
    console.error('Error sending lead follow-up email:', error);
    throw error;
  }
};
